import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiPhone, FiCopy, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';

const CallCenterErrorModal = ({ isOpen, toggle, errorData }) => {
  const [copiedSection, setCopiedSection] = useState(null);
  const copiedResetTimerRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copiedResetTimerRef.current) {
        clearTimeout(copiedResetTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const previouslyFocused = document.activeElement;
    modalRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        toggle();
        return;
      }
      if (e.key !== 'Tab') {
        return;
      }
      const focusable = [
        ...(modalRef.current ? [modalRef.current] : []),
        ...Array.from(
          modalRef.current?.querySelectorAll(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          ) ?? []
        )
      ];
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === focusable[1]) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus();
    };
  }, [isOpen, toggle]);

  const formatTime = (time) => {
    if (!time) {
      return 'N/A';
    }
    if (typeof time === 'string') {
      const combinedPattern = /^(\d{4})-(\d{2})-(\d{2})\s+(.+)$/;
      const match = time.trim().match(combinedPattern);
      if (match) {
        const [, year, month, day, timeStr] = match;
        const dateStr = `${month}/${day}/${year} ${timeStr}`;
        try {
          const date = new Date(dateStr);
          if (!Number.isNaN(date.getTime())) {
            return date.toLocaleString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            });
          }
        } catch {
          // Fall through to generic Date parsing
        }
      }
    }
    try {
      const date = new Date(time);
      if (Number.isNaN(date.getTime())) {
        return String(time);
      }
      return date.toLocaleString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return time;
    }
  };

  const getErrorTypeInfo = () => {
    if (!errorData) {
      return null;
    }

    const { errorType } = errorData;

    switch (errorType) {
      case 'LEAD_SAVE_FAILED':
        return {
          title: 'Unable to Save Lead Information',
          severity: 'warning',
          instructions: [
            'Transfer the call to the company directly',
            'Inform the customer that a specialist will assist them',
            'Provide the company phone number if requested'
          ],
          action: 'Transfer Call',
          showAppointment: false
        };

      case 'SLOTS_FETCH_FAILED':
        return {
          title: 'Unable to Load Available Appointments',
          severity: 'warning',
          instructions: [
            'The appointment system is temporarily unavailable',
            'Transfer the call to the company for manual scheduling',
            'Apologize for the inconvenience'
          ],
          action: 'Transfer Call',
          showAppointment: false
        };

      case 'APPOINTMENT_SAVE_FAILED':
      case 'CRM_UNAVAILABLE':
        return {
          title: 'Unable to Save Appointment',
          severity: 'error',
          instructions: [
            'Copy all information below before ending the call',
            'Contact the company after the call ends',
            'Provide them with the lead and appointment details',
            'Request manual entry into their system'
          ],
          action: 'Copy & Contact Company',
          showAppointment: true
        };

      default:
        return {
          title: 'System Error',
          severity: 'error',
          instructions: ['An unexpected error occurred', 'Please contact the company directly'],
          action: 'Contact Company',
          showAppointment: false
        };
    }
  };

  const errorInfo = getErrorTypeInfo();

  const clipboardApiBlockedRef = useRef(false);

  const copyToClipboard = (text, section) => {
    const fallbackCopy = () => {
      let textarea;
      try {
        textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const successful = document.execCommand('copy');
        return successful;
      } catch (e) {
        console.error('Fallback copy failed:', e);
        return false;
      } finally {
        if (textarea && textarea.parentNode) {
          textarea.parentNode.removeChild(textarea);
        }
      }
    };
    const handleSuccess = () => {
      setCopiedSection(section);
      if (copiedResetTimerRef.current) {
        clearTimeout(copiedResetTimerRef.current);
      }
      copiedResetTimerRef.current = setTimeout(() => setCopiedSection(null), 3000);
    };
    if (!clipboardApiBlockedRef.current && navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(text)
        .then(handleSuccess)
        .catch((err) => {
          if (err?.name === 'NotAllowedError') {
            clipboardApiBlockedRef.current = true;
          } else {
            console.error('Failed to copy via Clipboard API:', err);
          }

          const ok = fallbackCopy();
          if (ok) {
            handleSuccess();
          } else {
            alert(
              'Copy to clipboard is blocked in this environment.\n' +
                'Please manually highlight the text and press Ctrl+C (Cmd+C on Mac).'
            );
          }
        });
    } else {
      const ok = fallbackCopy();
      if (ok) {
        handleSuccess();
      } else {
        alert(
          'Copy to clipboard is blocked in this environment.\n' +
            'Please manually highlight the text and press Ctrl+C (Cmd+C on Mac).'
        );
      }
    }
  };

  const formatLeadInfo = () => {
    if (!errorData?.leadSummary) {
      return '';
    }

    const lead = errorData.leadSummary;
    return `Lead Information:
Name: ${lead.name || 'N/A'}
Phone: ${lead.phone || 'N/A'}
Email: ${lead.email || 'N/A'}
Address: ${lead.address1 || 'N/A'}
City: ${lead.city || 'N/A'}
State: ${lead.state || 'N/A'}
ZIP: ${lead.zip || 'N/A'}
Product Interest: ${lead.product || 'N/A'}`;
  };

  const formatAppointmentInfo = () => {
    if (!errorData?.appointmentSummary) {
      return '';
    }

    const appt = errorData.appointmentSummary;

    return `Appointment Details:
Date & Time: ${formatTime(appt.sliceStartTime)}
Block ID: ${appt.blockId || 'N/A'}`;
  };

  const formatAllInfo = () => {
    const parts = [];
    const leadInfo = formatLeadInfo();
    if (leadInfo) {
      parts.push(leadInfo);
    }
    if (errorInfo?.showAppointment && errorData?.appointmentSummary) {
      parts.push(formatAppointmentInfo());
    }
    if (errorData?.orgContact) {
      parts.push(`Company Contact:
${errorData.orgContact.name || 'Company'}
Phone: ${errorData.orgContact.phone || 'N/A'}`);
    }
    return parts.join('\n\n');
  };

  if (!isOpen || !errorData || !errorInfo) {
    return null;
  }

  return createPortal(
    <>
      <div className="modal-backdrop fade show" />
      <div
        ref={modalRef}
        className="modal fade show d-block call-center-error-modal"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        aria-labelledby="callCenterErrorModalTitle">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className={`modal-header error-header-${errorInfo.severity}`}>
              <h5 id="callCenterErrorModalTitle" className="modal-title">
                <FiAlertCircle className="error-icon" />
                {errorInfo.title}
              </h5>
              <button type="button" className="btn-close" onClick={toggle} aria-label="Close" />
            </div>

            <div className="modal-body">
              <div className="instructions-section">
                <h5>Instructions for Call Center Representative:</h5>
                <ol className="instructions-list">
                  {errorInfo.instructions.map((instruction, index) => (
                    <li key={index}>{instruction}</li>
                  ))}
                </ol>
              </div>

              {errorData?.orgContact && (
                <div className="contact-section">
                  <h5>Company Contact Information:</h5>
                  <div className="contact-card">
                    <div className="contact-info">
                      <strong>{errorData.orgContact.name || 'Company'}</strong>
                      <div className="phone-number">
                        <FiPhone className="phone-icon" />
                        {errorData.orgContact.phone ? (
                          <a href={`tel:${errorData.orgContact.phone}`} className="phone-link">
                            {errorData.orgContact.phone}
                          </a>
                        ) : (
                          <span>N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {errorData?.leadSummary && (
                <div className="info-section">
                  <div className="section-header">
                    <h5>Lead Information:</h5>
                    <button
                      className="btn btn-link btn-sm copy-button"
                      onClick={() => copyToClipboard(formatLeadInfo(), 'lead')}>
                      {copiedSection === 'lead' ? (
                        <>
                          <FiCheckCircle /> Copied!
                        </>
                      ) : (
                        <>
                          <FiCopy /> Copy Lead Info
                        </>
                      )}
                    </button>
                  </div>
                  <div className="info-box">
                    <div className="info-row">
                      <span className="info-label">Name:</span>
                      <span className="info-value">{errorData.leadSummary.name || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Phone:</span>
                      <span className="info-value">{errorData.leadSummary.phone || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{errorData.leadSummary.email || 'N/A'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Address:</span>
                      <span className="info-value">
                        {errorData.leadSummary.address1 || 'N/A'}, {errorData.leadSummary.city || ''}{' '}
                        {errorData.leadSummary.state || ''} {errorData.leadSummary.zip || ''}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Product:</span>
                      <span className="info-value">{errorData.leadSummary.product || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

              {errorInfo.showAppointment && errorData?.appointmentSummary && (
                <div className="info-section">
                  <div className="section-header">
                    <h5>Appointment Details:</h5>
                    <button
                      className="btn btn-link btn-sm copy-button"
                      onClick={() => copyToClipboard(formatAppointmentInfo(), 'appointment')}>
                      {copiedSection === 'appointment' ? (
                        <>
                          <FiCheckCircle /> Copied!
                        </>
                      ) : (
                        <>
                          <FiCopy /> Copy Appointment
                        </>
                      )}
                    </button>
                  </div>
                  <div className="info-box appointment-box">
                    <div className="info-row">
                      <span className="info-label">Date & Time:</span>
                      <span className="info-value">
                        {(() => {
                          if (!errorData.appointmentSummary.sliceStartTime) {
                            return 'N/A';
                          }
                          try {
                            return formatTime(errorData.appointmentSummary.sliceStartTime);
                          } catch {
                            return errorData.appointmentSummary.sliceStartTime;
                          }
                        })()}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Block ID:</span>
                      <span className="info-value">{errorData.appointmentSummary.blockId || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer modal-footer-buttons">
              <button
                className="btn btn-primary copy-all-button"
                onClick={() => copyToClipboard(formatAllInfo(), 'all')}>
                {copiedSection === 'all' ? (
                  <>
                    <FiCheckCircle /> All Information Copied!
                  </>
                ) : (
                  <>
                    <FiCopy /> Copy All Information
                  </>
                )}
              </button>
              <button className="btn btn-secondary" onClick={toggle}>
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default CallCenterErrorModal;
